/**
 * Solve One-Step Equations generator (6.PAFR.1.3).
 *
 * Builds a one-step equation around a known solution using the equation-simple
 * engine, then verifies by solving independently. The engine also produces the
 * displayed equation, the worked steps, and validates that solving reproduces
 * the chosen solution, so the prompt and graded answer are always consistent.
 *
 * Families map to the four one-step operations:
 *   F1: x + b = c        (subtract to undo)
 *   F2: x - b = c        (add to undo)
 *   F3: b * x = c        (divide to undo)
 *   F4: x / b = c        (multiply to undo)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import {
  buildEquation,
  solveEquation,
  equationToLatex,
  equationToText,
  deriveSteps,
  type OneStepOperation,
} from "../engine/equation-simple";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "solve-equations" as const;
const VERSION = "1.0.0";

const OP_FAMILY: Record<string, OneStepOperation> = {
  F1: "add",
  F2: "subtract",
  F3: "multiply",
  F4: "divide",
};

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly operandMax: number;
  readonly solMin: number;
  readonly solMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], operandMax: 9, solMin: 1, solMax: 12 },
  standard: { families: ["F1", "F2", "F3", "F4"], operandMax: 12, solMin: 1, solMax: 15 },
  hard: { families: ["F1", "F2", "F3", "F4"], operandMax: 12, solMin: -12, solMax: 15 },
  challenge: { families: ["F3", "F4"], operandMax: 15, solMin: -15, solMax: 15 },
};

const VARS = ["x", "y", "n", "m"] as const;

export class SolveEquationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const operation = OP_FAMILY[family] as OneStepOperation;
      const v = rng.pick(VARS);

      const operand = rat(rng.int(2, cfg.operandMax));
      const solution = rat(rng.intNonZero(cfg.solMin, cfg.solMax));

      const eq = buildEquation(v, operation, operand, solution);

      // Independent verification: solving must reproduce the chosen solution.
      const solved = solveEquation(eq);
      if (!solved.equals(solution)) continue;
      // Division problems: require the "result" to stay a tidy integer.
      if (operation === "divide" && !eq.result.isInteger()) continue;

      const steps = deriveSteps(eq);

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Solve for ${v}: ${equationToText(eq)}`,
          latex: equationToLatex(eq),
        },
        answerType: "number",
        answer: { kind: "number", value: solution },
        steps,
        hints: [
          { level: 1, text: "Use the inverse operation to undo what is done to the variable." },
          {
            level: 2,
            text: "Whatever you do to one side, do to the other so the equation stays balanced.",
          },
        ],
        subskills: topic.subskills,
        math: eq,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const solveEquations = new SolveEquationsGenerator();
